/**
 * Market Data Utilities
 *
 * Shared helpers for computing initial chart ranges and fetching bar data
 * from the market-data-api BFF proxy.
 */

export interface Bar {
  time: number; // milliseconds
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface InitialRange {
  from: number; // unix seconds
  to: number;   // unix seconds
  isExplicitRange: boolean;
}

export interface FetchResult {
  bars: Bar[];
  coveredFrom: number | null; // ms timestamp of first bar
  coveredTo: number | null;   // ms timestamp of last bar
  completion: 'complete' | 'truncated_by_budget';
}

// Nominal windows in seconds per interval
const NOMINAL_WINDOWS: Record<string, number> = {
  '1d': 150 * 86400,   // ~150 days
  '4h': 30 * 86400,    // ~30 days
  '1h': 7 * 86400,     // ~7 days
  '30m': 4 * 86400,    // ~4 days
  '15m': 2 * 86400,    // ~2 days
  '5m': 12 * 3600,     // ~12 hours
  '1m': 3 * 3600,      // ~3 hours
};

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function parseDateAsUTC(dateStr: string): number {
  return new Date(dateStr + 'T00:00:00Z').getTime() / 1000;
}

/**
 * Check that a YYYY-MM-DD string represents an actual calendar date
 * (not an overflow like 2025-02-31 which JS silently rolls to Mar 3).
 */
function isValidCalendarDate(dateStr: string): boolean {
  const d = new Date(dateStr + 'T00:00:00Z');
  if (isNaN(d.getTime())) return false;
  // Round-trip: the parsed date must produce the same YYYY-MM-DD
  return d.toISOString().startsWith(dateStr);
}

/**
 * Normalize a date-like string to YYYY-MM-DD.
 * Accepts plain dates ("2024-01-15") and ISO datetimes ("2024-01-15T10:30:00Z").
 * ISO datetimes are accepted with a console warning; the date portion is extracted.
 * Returns null for unrecognizable formats or non-existent calendar dates
 * (e.g. 2025-02-31, 2025-13-01).
 */
export function normalizeDate(input: string): string | null {
  let candidate: string | null = null;

  if (DATE_REGEX.test(input)) {
    candidate = input;
  } else if (input.includes('T')) {
    const datePart = input.split('T')[0];
    if (DATE_REGEX.test(datePart)) {
      console.warn(`[market-data-utils] ISO datetime received, extracting date portion: ${input}`);
      candidate = datePart;
    }
  }

  if (candidate && isValidCalendarDate(candidate)) return candidate;
  return null;
}

/**
 * Compute initial range for chart data fetching.
 *
 * When both start and end are provided, returns date strings with isExplicitRange=true.
 * Otherwise, returns unix timestamps (seconds) with isExplicitRange=false.
 */
export function computeInitialRange(
  interval: string,
  start?: string,
  end?: string
): InitialRange {
  const window = NOMINAL_WINDOWS[interval] || NOMINAL_WINDOWS['1d'];

  // Normalize and validate dates
  const normalizedStart = start ? normalizeDate(start) : null;
  const normalizedEnd = end ? normalizeDate(end) : null;

  // Both provided → explicit range (converted to unix seconds for API compatibility)
  if (normalizedStart && normalizedEnd) {
    const startUnix = parseDateAsUTC(normalizedStart);
    // End date is made inclusive by adding one day (cover the full end day for intraday)
    let endUnix = parseDateAsUTC(normalizedEnd) + 86400;
    // Ensure non-zero range even when start === end
    if (endUnix <= startUnix) endUnix = startUnix + 86400;
    return { from: startUnix, to: endUnix, isExplicitRange: true };
  }

  const now = Math.floor(Date.now() / 1000);

  // Neither provided
  if (!normalizedStart && !normalizedEnd) {
    return { from: now - window, to: now, isExplicitRange: false };
  }

  // Start only → [start, start + window]
  if (normalizedStart && !normalizedEnd) {
    const startUnix = parseDateAsUTC(normalizedStart);
    return { from: startUnix, to: startUnix + window, isExplicitRange: false };
  }

  // End only → [end - window, end]
  const endUnix = parseDateAsUTC(normalizedEnd!);
  return { from: endUnix - window, to: endUnix, isExplicitRange: false };
}

/**
 * Returns the nominal window (seconds) for a given interval.
 */
export function getNominalWindow(interval: string): number {
  return NOMINAL_WINDOWS[interval] || NOMINAL_WINDOWS['1d'];
}

interface FetchBarsOptions {
  maxBars?: number;
}

interface MarketDataItem {
  timestamp: string;
  open: string;
  high: string;
  low: string;
  close: string;
  volume: number;
}

interface MarketDataResponse {
  items?: MarketDataItem[];
  bars?: MarketDataItem[];
  has_more?: boolean;
  next_cursor?: string | null;
}

function parseItem(item: MarketDataItem): Bar {
  return {
    time: new Date(item.timestamp).getTime(),
    open: parseFloat(item.open),
    high: parseFloat(item.high),
    low: parseFloat(item.low),
    close: parseFloat(item.close),
    volume: item.volume,
  };
}

/**
 * Fetch bars from the BFF proxy for a given range.
 *
 * Handles:
 * - Single-page daily responses (already ascending, no pagination)
 * - Multi-page intraday responses (descending, reversed after collection)
 * - maxBars budget enforcement (stops pagination early or truncates)
 */
export async function fetchBarsForRange(
  assetType: string,
  symbol: string,
  interval: string,
  from: number | string,
  to: number | string,
  options?: FetchBarsOptions
): Promise<FetchResult> {
  const maxBars = options?.maxBars;
  const isIntraday = interval !== '1d';

  const encodedAssetType = encodeURIComponent(assetType);
  const encodedSymbol = encodeURIComponent(symbol);
  const isIndexIntraday = assetType === 'index' && isIntraday;
  const baseUrl = isIndexIntraday
    ? `/api/market-data/${encodedAssetType}/${encodedSymbol}/intraday`
    : `/api/market-data/${encodedAssetType}/${encodedSymbol}/${encodeURIComponent(interval)}`;
  const baseParams = new URLSearchParams();
  baseParams.set('start', String(from));
  baseParams.set('end', String(to));
  if (isIndexIntraday) baseParams.set('interval', interval);

  const allBars: Bar[] = [];
  let cursor: string | null = null;
  let truncated = false;

  // Fetch pages
  do {
    const params = new URLSearchParams(baseParams);
    if (cursor) params.set('cursor', cursor);

    const response = await fetch(`${baseUrl}?${params.toString()}`);
    if (!response.ok) {
      // Re-throw with status info for caller to handle
      const error = new Error(`Market data fetch failed: ${response.status}`);
      (error as Error & { status: number }).status = response.status;
      throw error;
    }

    const data: MarketDataResponse = await response.json();
    const items = data.items ?? data.bars ?? [];
    const pageBars = items.map(parseItem);
    allBars.push(...pageBars);

    // Check maxBars budget
    if (maxBars && allBars.length >= maxBars) {
      truncated = true;
      break;
    }

    cursor = data.has_more && data.next_cursor ? data.next_cursor : null;
  } while (cursor);

  // Intraday arrives descending → reverse to ascending
  let bars = isIntraday ? allBars.reverse() : allBars;

  // Enforce maxBars: keep most recent bars
  if (maxBars && bars.length > maxBars) {
    truncated = true;
    bars = bars.slice(bars.length - maxBars);
  }

  return {
    bars,
    coveredFrom: bars.length > 0 ? bars[0].time : null,
    coveredTo: bars.length > 0 ? bars[bars.length - 1].time : null,
    completion: truncated ? 'truncated_by_budget' : 'complete',
  };
}

/**
 * Convert a YYYY-MM-DD date string to a unix timestamp (seconds) suitable
 * for TradingView's setVisibleRange.
 *
 * mode='start': beginning of that UTC day (00:00:00Z)
 * mode='endInclusive': beginning of the NEXT UTC day (makes end date inclusive)
 */
export function dateToViewportUnix(dateStr: string, mode: 'start' | 'endInclusive'): number {
  const ts = parseDateAsUTC(dateStr);
  return mode === 'endInclusive' ? ts + 86400 : ts;
}
